
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/auth";
import { supabase } from "@/integrations/supabase/client";
import { pingSupabase, pingEdgeFunction } from "@/utils/supabase";
import { SUPABASE_ANON_KEY, getFunctionsUrl } from "@/utils/supabaseConfig";

export function useSubscription() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshPremiumStatus } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [connectivityError, setConnectivityError] = useState<boolean>(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [diagnosticInfo, setDiagnosticInfo] = useState<Record<string, any>>({});

  // Check for success or canceled URL parameters
  useEffect(() => {
    // Check if the URL contains success or canceled parameters
    const urlParams = new URLSearchParams(location.search);
    const isSuccess = urlParams.get('success') === 'true';
    const isCanceled = urlParams.get('canceled') === 'true';
    
    if (isSuccess) {
      toast.success("Payment successful! Your premium subscription is now active.");
      refreshPremiumStatus();
      
      // Clean up URL params
      navigate('/premium', { replace: true });
    } else if (isCanceled) {
      toast.info("Payment canceled. Your subscription was not processed.");
      
      // Clean up URL params
      navigate('/premium', { replace: true });
    }
  }, [location, navigate, refreshPremiumStatus]);

  const gatherDiagnosticInfo = async () => {
    try {
      const info: Record<string, any> = {
        timestamp: new Date().toISOString(),
        browser: navigator.userAgent,
        url: window.location.href,
        isSecureContext: window.isSecureContext,
        localStorage: !!window.localStorage,
        onlineStatus: navigator.onLine,
        screenWidth: window.innerWidth,
        auth: {
          hasUser: !!user
        }
      };

      try {
        const { success, error } = await pingSupabase();
        info.supabaseConnection = {
          pingSuccess: success,
          error: error ? error.message : null
        };
      } catch (e) {
        info.supabaseConnection = {
          pingSuccess: false,
          error: e.message || 'Unknown error pinging Supabase'
        };
      }

      try {
        const edgeFunctionResult = await pingEdgeFunction();
        info.edgeFunctionConnection = {
          pingSuccess: edgeFunctionResult.success,
          error: edgeFunctionResult.error ? edgeFunctionResult.error.message : null,
          data: edgeFunctionResult.data
        };
      } catch (e) {
        info.edgeFunctionConnection = {
          pingSuccess: false,
          error: e.message || 'Unknown error pinging Edge Function'
        };
      }

      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        info.serviceWorkers = registrations.length;
      }

      info.supabaseProjectId = import.meta.env.VITE_SUPABASE_PROJECT_ID || 'Not available';
      info.supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'Not available';

      setDiagnosticInfo(info);
      return info;
    } catch (error) {
      console.error('Error gathering diagnostic info:', error);
      return { error: error.message };
    }
  };

  const testEdgeFunctionConnectivity = async () => {
    try {
      setIsTestingConnection(true);
      setConnectivityError(false);
      setErrorDetails(null);
      
      console.log('Testing connectivity to edge function...');
      
      const diagnosticInfo = await gatherDiagnosticInfo();
      
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        const authError = "No active session found. Please log in again.";
        setErrorDetails(authError);
        setConnectivityError(true);
        setIsTestingConnection(false);
        toast.error(authError);
        return false;
      }
      
      const edgeFunctionResult = await pingEdgeFunction();
      diagnosticInfo.edgeFunctionResult = edgeFunctionResult;
      
      if (!edgeFunctionResult.success) {
        console.error('Edge function connectivity test failed:', edgeFunctionResult.error);
        setErrorDetails(JSON.stringify({
          message: edgeFunctionResult.error?.message || 'Unknown error',
          name: edgeFunctionResult.error?.name || 'Error',
          diagnosticInfo
        }, null, 2));
        
        setConnectivityError(true);
        setDiagnosticInfo(diagnosticInfo);
        setIsTestingConnection(false);
        return false;
      }
      
      console.log('Connectivity test response:', edgeFunctionResult.data);
      toast.success('Successfully connected to payment service!');
      setDiagnosticInfo(diagnosticInfo);
      setIsTestingConnection(false);
      return true;
    } catch (error) {
      console.error('Edge function connectivity test failed:', error);
      setErrorDetails(JSON.stringify({
        message: error.message,
        name: error.name,
        stack: error.stack,
        diagnosticInfo
      }, null, 2));
      setConnectivityError(true);
      setIsTestingConnection(false);
      return false;
    }
  };

  const checkEdgeFunctionStatus = async () => {
    setIsTestingConnection(true);
    const info = await gatherDiagnosticInfo();
    
    try {
      const functionUrl = 'https://mnfwgdxgthnojhcufndt.supabase.co/functions/v1/create-checkout';
      const response = await fetch(functionUrl, {
        method: 'OPTIONS'
      });
      
      info.edgeFunctionStatus = {
        directAccessStatus: response.status,
        directAccessOk: response.ok,
        directAccessStatusText: response.statusText
      };
    } catch (error) {
      info.edgeFunctionStatus = {
        directAccessError: error.message,
        directAccessErrorName: error.name
      };
    }
    
    setDiagnosticInfo(info);
    setIsTestingConnection(false);
    setErrorDetails(JSON.stringify(info, null, 2));
  };

  const handleSubscribe = async () => {
    if (!user) {
      toast.error("Please log in to subscribe");
      navigate('/login', { state: { redirect: '/premium' } });
      return;
    }
    
    setIsLoading(true);
    setErrorDetails(null);
    
    try {
      console.log('Starting checkout process...');
      
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }
      
      const isConnected = await testEdgeFunctionConnectivity();
      if (!isConnected) {
        throw new Error('Unable to connect to payment service. Please try again later or contact support.');
      }
      
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        throw new Error('No active session found. Please log in again');
      }
      
      console.log('Invoking create-checkout function...');
      
      const baseUrl = getFunctionsUrl();
      const apiKey = SUPABASE_ANON_KEY;
      const authHeader = sessionData.session.access_token ? `Bearer ${sessionData.session.access_token}` : undefined;
      const appOrigin = window.location.origin;
      
      // Get user email for better fraud prevention
      const email = user?.email || '';
      
      const response = await fetch(`${baseUrl}/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': apiKey,
          'Authorization': authHeader,
          'x-client-info': 'supabase-js-web/2.49.3',
          'x-deno-subhost': 'supabase-edge-functions'
        },
        body: JSON.stringify({
          successUrl: `${appOrigin}/premium?success=true`,
          cancelUrl: `${appOrigin}/premium?canceled=true`,
          email: email
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Checkout error:', response.status, errorText);
        throw new Error(`Failed to create checkout session: ${errorText}`);
      }
      
      const data = await response.json();
      
      if (!data || !data.url) {
        console.error('No checkout URL returned:', data);
        setErrorDetails(JSON.stringify(data, null, 2));
        throw new Error('Invalid response from checkout service');
      }
      
      console.log('Redirecting to checkout:', data.url);
      window.location.href = data.url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      
      let userMessage = "Failed to start checkout process. Please try again.";
      
      if (error.message.includes('Unable to connect')) {
        userMessage = "Unable to connect to payment service. Please check your network connection and try again.";
      } else if (error.message.includes('No active session')) {
        userMessage = "Your session has expired. Please log in again.";
        navigate('/login', { state: { redirect: '/premium' } });
      } else if (error.message.includes('Failed to send a request')) {
        userMessage = "Network issue detected. Please ensure you're connected to the internet and try again.";
      }
      
      toast.error(userMessage);
      setIsLoading(false);
    }
  };

  const clearErrorDetails = () => {
    setErrorDetails(null);
  };

  return {
    isLoading,
    errorDetails,
    connectivityError,
    isTestingConnection,
    diagnosticInfo,
    handleSubscribe,
    testEdgeFunctionConnectivity,
    checkEdgeFunctionStatus,
    clearErrorDetails
  };
}
